import { deleteItem, readItem, updateItem } from '@directus/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Schema } from '../../../../types/DirectusSchema';
import { eventMapper } from '../../mappers/event';
import { directusClient } from '../../services/directusClient/directusClient.ts';
import { tanstackQueryClient } from '../../services/tanstackQueryClient.ts';

export const useEventItem = (eventId: string) => {
  const query = useQuery({
    staleTime: 60 * (60 * 1000), // 60 mins
    queryKey: ['events', { eventId }, 'to-form'],
    queryFn: async () => {
      if (!eventId) {
        return null;
      }

      const data = await directusClient.request(
        readItem('events', eventId, {
          fields: [
            '*',
            {
              venue: ['id', 'commercial_name'],
              category: ['id', 'title'],
              target_audiences: ['*'],
            },
          ],
        }),
      );

      return eventMapper.apiToForm(data as unknown as Schema['events'][0]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updateData: Parameters<(typeof eventMapper)['formToApi']>[0]) => {
      if (!eventId || !query.data) {
        return { changes: false };
      }

      const apiData = eventMapper.formToApi(updateData);

      await directusClient.request(updateItem('events', eventId, apiData));
    },
    onSuccess: async () => {
      await tanstackQueryClient.invalidateQueries({
        queryKey: ['events', { eventId }],
        exact: false,
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      if (!eventId) {
        return;
      }

      await directusClient.request(deleteItem('events', eventId));
    },
    onSuccess: async () => {
      tanstackQueryClient.removeQueries({ queryKey: ['events', { eventId }] });
      await tanstackQueryClient.invalidateQueries({
        queryKey: ['events'],
        exact: false,
      });
    },
  });

  return { updateMutation, query, removeMutation };
};
